import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {NotificationApiModel, NotificationApiModelStatusEnum, NotificationsApiService} from '@npck/af-frontend-api';
import {debounceTime, Subject, Subscription} from 'rxjs';
import {FilterComponent, FilterEvent, FilterField} from '../../../components/full-table/filter/filter.component';
import {TableEvent} from '../../../components/full-table/full-table.component';
import {
    INITIAL_PAGE_NUMBER,
    INITIAL_PAGE_SIZE,
    PageEvent,
    PaginatorComponent,
} from '../../../components/full-table/paging/paginator.component';
import {NotificationsService} from '../../../domain/notifications.service';
import {NotificationItemComponent} from './notification-item/notification-item.component';

export const initialNotificationEvent: TableEvent = {
    page: {
        pageSize: INITIAL_PAGE_SIZE,
        pageNumber: INITIAL_PAGE_NUMBER,
        pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE,
    },
    spinnerId: 'initial',
};

@Component({
    selector: 'af-notification',
    templateUrl: './notification.component.html',
    standalone: false,
})
export class NotificationComponent implements OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('filter') filter: FilterComponent;
    @ViewChild('paginator') paginator: PaginatorComponent;
    @ViewChild('scrollWrapper', {read: ElementRef}) scrollWrapper: ElementRef<HTMLDivElement>;
    @ViewChild('sentinel', {read: ElementRef}) sentinel: ElementRef<HTMLDivElement>;
    @ViewChildren('notificationItem') notificationItems: QueryList<NotificationItemComponent>;
    @Input() filterFields: FilterField[];
    @Input() filterEvent: FilterEvent;
    @Input() notifications: NotificationApiModel[];
    @Input() pageEvent: PageEvent;
    @Input() totalElements: number;
    @Input() infiniteScroll = false;
    @Input() hasMore = false;
    @Input() markOnVisible = false;
    @Output() emptyStateClickListener = new EventEmitter<void>();
    @Output() notificationChangesListener = new EventEmitter<NotificationEvent>();
    @Output() loadMoreListener = new EventEmitter<void>();

    spinnerIds: string[] | string = ['initial', 'filter', 'paging'];

    notificationEvent = <NotificationEvent>{
        spinnerId: 'initial',
        page: {
            pageNumber: INITIAL_PAGE_NUMBER,
            pageSize: INITIAL_PAGE_SIZE,
        },
    };

    private readObserver?: IntersectionObserver;
    private loadMoreObserver?: IntersectionObserver;
    private readonly itemByElement = new Map<Element, NotificationItemComponent>();
    private readonly pendingReadIds = new Set<string>();
    private readonly pendingRead$ = new Subject<void>();
    private readSubscription?: Subscription;

    constructor(
        private notificationsApiService: NotificationsApiService,
        private notificationsService: NotificationsService,
    ) {
    }

    get emptyStateTitle(): string {
        return this.notificationEvent.spinnerId === 'initial' &&
            !this.notifications.length &&
            'i18n_af_emptyStateTitleNotification' || 'i18n_af_emptyStateTitleSearchResult';
    }

    get emptyStateSubtitle(): string {
        return this.notificationEvent.spinnerId === 'initial' &&
            !this.notifications.length &&
            'i18n_af_emptyStateSubtitleNoData' || 'i18n_af_emptyStateSubtitleSearchResult';
    }

    get emptyStateIcon(): string {
        return this.notificationEvent.spinnerId === 'initial' &&
            !this.notifications.length &&
            'ic_empty_state_notification' || 'ic_empty_state_not_found';
    }

    get emptyStateButton(): string {
        return this.notificationEvent.spinnerId === 'initial' &&
            !this.notifications.length &&
            '' || this.notificationEvent.spinnerId !== 'initial' && 'i18n_af_buttonNewSearch';
    }

    ngAfterViewInit(): void {
        if (this.markOnVisible) {
            this.readObserver = new IntersectionObserver(
                entries => {
                    for (const entry of entries) {
                        if (!entry.isIntersecting) {
                            continue;
                        }
                        const item = this.itemByElement.get(entry.target);
                        if (item?.notification?.status === NotificationApiModelStatusEnum.not_viewed && item.notification.id) {
                            this.pendingReadIds.add(item.notification.id);
                            this.readObserver?.unobserve(entry.target);
                            this.itemByElement.delete(entry.target);
                            this.pendingRead$.next();
                        }
                    }
                },
                {root: this.scrollWrapper?.nativeElement ?? null, threshold: 0.6},
            );
            this.readSubscription = this.pendingRead$.pipe(debounceTime(400)).subscribe(() => this.flushRead());

            this.observeItems();
            this.notificationItems.changes.subscribe(() => this.observeItems());
        }

        if (this.infiniteScroll) {
            this.loadMoreObserver = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && this.hasMore) {
                        this.loadMoreObserver?.unobserve(this.sentinel.nativeElement);
                        this.loadMoreListener.emit();
                    }
                },
                {root: this.scrollWrapper?.nativeElement ?? null, threshold: 0},
            );
            if (this.sentinel) {
                this.loadMoreObserver.observe(this.sentinel.nativeElement);
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.notifications) {
            this.observeItems();
            if (this.infiniteScroll && this.hasMore && this.loadMoreObserver && this.sentinel) {
                this.loadMoreObserver.observe(this.sentinel.nativeElement);
            }
        }
    }

    ngOnDestroy(): void {
        this.readObserver?.disconnect();
        this.loadMoreObserver?.disconnect();
        this.readSubscription?.unsubscribe();
    }

    filterData(event: FilterEvent): void {
        this.paginator?.reset();
        this.notificationEvent = {
            ...this.notificationEvent,
            filter: event,
            spinnerId: 'filter',
        };
        this.notificationChangesListener.emit(this.notificationEvent);
    }


    pageChanges(event: PageEvent): void {
        this.notificationEvent = {
            ...this.notificationEvent,
            page: {
                pageSize: event.pageSize,
                pageNumber: event.pageNumber,
                pageOffset: event.pageOffset,
            },
            spinnerId: 'paging',
        };
        this.notificationChangesListener.emit(this.notificationEvent);
    }

    scrollToTop(shouldScroll: boolean): void {
        if (shouldScroll) {
            this.notificationItems?.first?.container?.nativeElement
                ?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    onEmptyStateClick(): void {
        if (this.notificationEvent.spinnerId === 'initial' &&
            !this.notifications.length &&
            this.emptyStateClickListener.observed) {
            this.emptyStateClickListener.emit();
        } else {
            this.filter.reset();
        }
    }

    private observeItems(): void {
        if (!this.readObserver) {
            return;
        }
        this.notificationItems.forEach(item => {
            const element = item.container?.nativeElement;
            if (element && !this.itemByElement.has(element)
                && item.notification?.status === NotificationApiModelStatusEnum.not_viewed) {
                this.itemByElement.set(element, item);
                this.readObserver?.observe(element);
            }
        });
    }

    private flushRead(): void {
        if (!this.pendingReadIds.size) {
            return;
        }
        const ids = [...this.pendingReadIds];
        this.pendingReadIds.clear();
        this.notificationsApiService.markNotificationsViewed({notificationDataIds: ids})
            .subscribe(() => {
                this.notifications.forEach(n => {
                    if (n.id && ids.includes(n.id)) {
                        n.status = NotificationApiModelStatusEnum.viewed;
                    }
                });
                this.notificationsService.decreaseUnreadCount(ids.length);
            });
    }
}

export interface NotificationEvent {
    filter?: FilterEvent;
    page?: PageEvent;
    spinnerId?: string;
}
